import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Header from "../../ext/Header";
import { Skeleton } from "../../ui/skeleton";
import SharedDataContext from "../../../SharedDataContext";
import SingleAccount from "./SingleAccount";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";
import { useStoreCRMSearch } from "../../../stores/useStoreCRMSearch";
import { useStoreCRMAnalyse } from "../../../stores/useStoreCRMAnalyse";
import { useCRMSearchRecords } from "../../../apiHooks/useCRMSearchRecords";
import { useCRMAnalyseRecords } from "../../../apiHooks/useCRMAnalyseRecords";
import { useStoreCRMConfig } from "../../../stores/useStoreCRMConfig";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import Image from "../../Tags/Image";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Checkbox } from "../../ui/checkbox";
import toast from "react-hot-toast";
import { ChevronDownIcon } from "lucide-react";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { useCsvUploader } from "../../../hooks/useCsvUploader";

const resolveAccountKey = (account, index) =>
  account?.organization?.id ||
  account?.company_id ||
  account?.companyId ||
  account?.company_linkedin_url ||
  account?.company_name ||
  `account-${index}`;

export default function BulkAccounts() {
  const { userData } = useContext(SharedDataContext);
  const workspaceId = userData?.activeWorkspace?.id || null;
  const activeWorkspace = userData?.activeWorkspace || null;
  const [accounts, setAccounts] = useState([]);
  const [resolvedOrganizations, setResolvedOrganizations] = useState({});
  const [orgSearchStatus, setOrgSearchStatus] = useState({});
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const captureEvent = useCaptureEvent();
  const { uploadCsv: uploadAccountsCsv, isCSVUploadLoading } = useCsvUploader(
    activeWorkspace,
    userData?.name,
    "Company"
  );

  const { activeCRMIntegration } = useStoreIntegrations();
  const { savedCompanyConfig, savedCompanyConfigWithWritePermission } = useStoreCRMConfig();

  const companyConfigs = Array.isArray(savedCompanyConfig) ? savedCompanyConfig : [];
  const companyConfigsWithPermissions = Array.isArray(savedCompanyConfigWithWritePermission)
    ? savedCompanyConfigWithWritePermission
    : [];

  const { setCRMBulkSearchPayload, setCRMBulkSearchData, crmBulkSearchData } = useStoreCRMSearch();

  const { setCRMBulkAnalysePayload, setCRMBulkAnalyseData, isCRMBulkAnalyseFetched } = useStoreCRMAnalyse();

  const { mutate: runCRMSearch } = useCRMSearchRecords();
  const { mutate: runCRMAnalyse } = useCRMAnalyseRecords();

  const searchTriggeredRef = useRef(false);
  const analyseTriggeredRef = useRef(false);

  useEffect(() => {
    window.parent.postMessage({ action: "IFRAME_READY" }, "*");
  }, []);

  useEffect(() => {
    const messageHandler = (event) => {
      const action = event?.data?.action;
      if (!action) return;

      if (action === "RECEIVE_PROFILES_DATA") {
        const accountsData = Array.isArray(event.data.data) ? event.data.data : [];
        setResolvedOrganizations({});
        setOrgSearchStatus({});
        searchTriggeredRef.current = false;
        analyseTriggeredRef.current = false;
        setCRMBulkSearchPayload([]);
        setCRMBulkSearchData([]);
        setCRMBulkAnalysePayload([]);
        setCRMBulkAnalyseData([]);
        setAccounts(accountsData);

        event.source?.postMessage({ action: "DATA_RECEIVED" }, "*");
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, []);

  const accountEntries = useMemo(
    () =>
      accounts.map((account, index) => {
        const key = resolveAccountKey(account, index);
        return {
          key,
          account,
          resolved: resolvedOrganizations[key],
        };
      }) || [],
    [accounts, resolvedOrganizations]
  );

  const accountEntryMap = useMemo(() => {
    const map = {};
    accountEntries.forEach((entry) => {
      map[entry.key] = entry;
    });
    return map;
  }, [accountEntries]);

  const selectedOrganizations = useMemo(() => {
    const normalizeLinkedinUrl = (value) => {
      if (!value) return "";
      const trimmed = String(value).trim();
      if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
      }
      const cleaned = trimmed.replace(/^company\//i, "");
      return `https://www.linkedin.com/company/${cleaned}`;
    };

    const uniqueOrgs = new Map();

    selectedAccountKeys.forEach((key) => {
      const entry = accountEntryMap[key];
      const organization = entry?.resolved?.organization;
      if (!organization?.id) {
        return;
      }

      const sourceAccount = entry?.account || {};
      const linkedinUrl = normalizeLinkedinUrl(
        organization.linkedin_url || sourceAccount?.company_linkedin_url || sourceAccount?.companyLinkedinUrl || null
      );
      const domain = organization.domain || sourceAccount?.company_domain || sourceAccount?.companyDomain || "";
      const name = organization.name || sourceAccount?.company_name || sourceAccount?.companyName || "Organization";

      uniqueOrgs.set(organization.id, {
        company_linkedin_url: linkedinUrl || "",
        company_name: name,
        company_domain: domain || "",
      });
    });

    return Array.from(uniqueOrgs.values());
  }, [accountEntryMap, selectedAccountKeys]);

  const resolvedOrganizationList = useMemo(
    () => accountEntries.map((entry) => entry.resolved).filter(Boolean),
    [accountEntries]
  );

  const allOrganizationsReady = useMemo(() => {
    if (accountEntries.length === 0) {
      return false;
    }
    return accountEntries.every((entry) => entry.resolved?.organization?.id);
  }, [accountEntries]);

  const allSearchCompleted = useMemo(() => {
    if (resolvedOrganizationList.length === 0) {
      return false;
    }
    return resolvedOrganizationList.every((item) => {
      const orgId = item.organization?.id;
      return orgId ? orgSearchStatus[orgId]?.completed : false;
    });
  }, [resolvedOrganizationList, orgSearchStatus]);

  const hasCRMMatches = useMemo(() => {
    if (resolvedOrganizationList.length === 0) {
      return false;
    }
    return resolvedOrganizationList.some((item) => {
      const orgId = item.organization?.id;
      return orgId ? Boolean(orgSearchStatus[orgId]?.crmRecordId) : false;
    });
  }, [resolvedOrganizationList, orgSearchStatus]);

  const selectableAccountKeys = useMemo(
    () => accountEntries.filter((entry) => entry.resolved?.organization?.id).map((entry) => entry.key),
    [accountEntries]
  );

  useEffect(() => {
    setSelectedAccountKeys((prev) =>
      prev.filter((key) => {
        const entry = accountEntryMap[key];
        return entry?.resolved?.organization?.id;
      })
    );
  }, [accountEntryMap]);

  const showSkeletons = accounts.length === 0;

  const handleOrgResolved = useCallback((payload, key) => {
    if (!payload?.organization?.id || !key) {
      return;
    }

    setResolvedOrganizations((prev) => {
      const existing = prev[key];
      if (
        existing &&
        existing.organization?.id === payload.organization.id &&
        existing.hasPendingEnrichmentAction === payload.hasPendingEnrichmentAction
      ) {
        return prev;
      }
      return {
        ...prev,
        [key]: payload,
      };
    });
  }, []);

  const handleAccountSelectionChange = useCallback((key, isChecked) => {
    setSelectedAccountKeys((prev) => {
      if (!isChecked) {
        return prev.filter((item) => item !== key);
      }
      if (prev.includes(key)) {
        return prev;
      }
      return [...prev, key];
    });
  }, []);

  const handleSelectAllChange = useCallback(
    (checked) => {
      if (checked) {
        setSelectedAccountKeys(selectableAccountKeys);
      } else {
        setSelectedAccountKeys([]);
      }

      // PostHog: Track select all action
      captureEvent("EXC_BulkAccounts_SelectAll", {
        action: checked ? "select" : "deselect",
        selected_count: checked ? selectableAccountKeys.length : 0,
        total_count: selectableAccountKeys.length,
      });
    },
    [selectableAccountKeys, captureEvent]
  );

  const handleExportCompanies = useCallback(() => {
    if (selectedAccountKeys.length === 0) {
      toast.error("Please select at least one company");
      return;
    }

    if (selectedOrganizations.length === 0) {
      toast.error("Selected companies are still processing. Please wait and try again.");
      return;
    }

    captureEvent("EXP_BulkAccounts_ExportCSV_SubmitClick", {
      companies_count: selectedOrganizations.length,
    });

    uploadAccountsCsv(selectedOrganizations);
  }, [captureEvent, selectedAccountKeys, selectedOrganizations, uploadAccountsCsv]);

  const selectedCount = selectedAccountKeys.length;
  const allSelectableSelected =
    selectableAccountKeys.length > 0 &&
    selectableAccountKeys.every((key) => selectedAccountKeys.includes(key));
  const isIndeterminate = selectedCount > 0 && !allSelectableSelected;
  const exportCount = selectedOrganizations.length || selectedCount;
  const exportLabel = exportCount > 0 ? `Export ${exportCount} companies` : "Export companies";
  const isExportDisabled = exportCount === 0 || isCSVUploadLoading;

  const handleOpenCompanyPush = useCallback(
    (companyConfig) => {
      if (selectedAccountKeys.length === 0) {
        toast.error("Please select at least one company");
        return;
      }

      if (!activeWorkspace?.slug) {
        toast.error("Workspace information unavailable");
        return;
      }

      const hasPermission =
        companyConfigsWithPermissions.length === 0 ||
        companyConfigsWithPermissions.find(
          (item) => item.crm_object_api_name === companyConfig?.crm_object_api_name
        );

      if (!hasPermission) {
        toast.error("You do not have permission to create this object");
        return;
      }

      // PostHog: Track push to CRM click
      captureEvent("EXC_BulkAccounts_PushToCRM_Click", {
        accounts_count: selectedAccountKeys.length,
        crm_object_label: companyConfig?.crm_object_label,
      });

      const organizationsPayload = Array.from(new Set(selectedAccountKeys))
        .map((key) => {
          const entry = accountEntryMap[key];
          const resolved = entry?.resolved?.organization;
          if (!resolved?.id) {
            return null;
          }

          const sourceAccount = entry?.account || {};
          const status = orgSearchStatus[resolved.id];

          return {
            organization_id: resolved.id,
            orgName: resolved.name || sourceAccount?.company_name || sourceAccount?.companyName || "",
            orgLinkedInUrl: resolved.linkedin_url || sourceAccount?.company_linkedin_url || "",
            orgDomain: resolved.domain || sourceAccount?.company_domain || "",
            crm_record_id: status?.crmRecordId || null,
          };
        })
        .filter(Boolean);

      const uniqueOrganizations = organizationsPayload.reduce((acc, org) => {
        if (!org) {
          return acc;
        }
        acc.set(org.organization_id, org);
        return acc;
      }, new Map());

      const uniqueOrganizationList = Array.from(uniqueOrganizations.values());

      if (uniqueOrganizationList.length === 0) {
        toast.error("Selected companies are still processing. Please wait and try again.");
        return;
      }

      const uniqueKey = `push_to_crm_payload_${Date.now()}`;
      const queryObject = {
        mode: "company",
        crm_object_api_name: companyConfig?.crm_object_api_name,
        crm_object_api_name_company: companyConfig?.crm_object_api_name,
        crm_object_label_company: companyConfig?.crm_object_label,
        organizations: uniqueOrganizationList,
        callbackUrl: "https://www.linkedin.com/company/lambdatest?tab=contacts",
      };

      try {
        localStorage.setItem(uniqueKey, JSON.stringify(queryObject));
      } catch (error) {
        console.error("Failed to save CRM payload to localStorage", error);
      }

      const pushWindow = window.open(
        `/${process.env.REACT_APP_APP_HOME}/${activeWorkspace.slug}/company-list/push-to-crm?payload=${uniqueKey}`,
        "_blank"
      );

      const handshakeListener = (event) => {
        if (event.data === "ready_for_payload") {
          pushWindow?.postMessage({ payloadKey: uniqueKey, payload: queryObject });
          window.removeEventListener("message", handshakeListener);
        }
      };

      window.addEventListener("message", handshakeListener);
      setSelectedAccountKeys([]);
    },
    [
      activeWorkspace?.slug,
      accountEntryMap,
      orgSearchStatus,
      selectedAccountKeys,
      companyConfigsWithPermissions,
      captureEvent,
    ]
  );

  useEffect(() => {
    if (!allOrganizationsReady || !workspaceId || !activeCRMIntegration?.id) {
      return;
    }

    if (searchTriggeredRef.current) {
      return;
    }

    const searchPayload = resolvedOrganizationList.map((item) => ({
      organization_id: item.organization.id,
      type: "company",
      edit_search: false,
    }));

    if (searchPayload.length === 0) {
      return;
    }

    setCRMBulkSearchPayload(searchPayload);
    setCRMBulkSearchData([]);
    const initialStatus = searchPayload.reduce((acc, item) => {
      acc[item.organization_id] = {
        completed: false,
        crmRecordId: null,
      };
      return acc;
    }, {});
    setOrgSearchStatus(initialStatus);

    runCRMSearch(
      {
        data: searchPayload,
        configId: activeCRMIntegration.id,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      },
      {
        onSuccess: (response) => {
          if (response?.data && Array.isArray(response.data)) {
            setCRMBulkSearchData(response.data);
          }
        },
        onSettled: () => {
          setOrgSearchStatus((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((orgId) => {
              if (!next[orgId]?.completed) {
                next[orgId] = {
                  ...next[orgId],
                  completed: true,
                };
              }
            });
            return next;
          });
        },
      }
    );
    searchTriggeredRef.current = true;
    analyseTriggeredRef.current = false;
  }, [
    activeCRMIntegration?.id,
    allOrganizationsReady,
    resolvedOrganizationList,
    runCRMSearch,
    setCRMBulkSearchData,
    setCRMBulkSearchPayload,
    workspaceId,
  ]);

  const flattenCRMRecords = useCallback((records = []) => {
    if (!Array.isArray(records)) {
      return [];
    }
    return records.flatMap((entry) => {
      if (!entry) return [];
      if (Array.isArray(entry.records)) {
        return entry.records.filter(Boolean);
      }
      return entry ? [entry] : [];
    });
  }, []);

  useEffect(() => {
    if (!crmBulkSearchData || crmBulkSearchData.length === 0) {
      return;
    }

    setOrgSearchStatus((prev) => {
      const next = { ...prev };
      crmBulkSearchData.forEach((entry) => {
        const orgId = entry?.organization_id;
        if (!orgId) {
          return;
        }

        const records = flattenCRMRecords(entry.records);
        const matched =
          records.find((record) => record?.match_confirmed) ||
          records.find((record) => record?.crm_record_id || record?.Id || record?.id) ||
          null;

        const crmRecordId = matched?.crm_record_id || matched?.Id || matched?.id || null;

        next[orgId] = {
          ...(next[orgId] || {}),
          completed: true,
          crmRecordId: crmRecordId ?? next[orgId]?.crmRecordId ?? null,
        };
      });
      return next;
    });
  }, [crmBulkSearchData, flattenCRMRecords]);

  useEffect(() => {
    if (
      !searchTriggeredRef.current ||
      analyseTriggeredRef.current ||
      !workspaceId ||
      !activeCRMIntegration?.id
    ) {
      return;
    }

    const allCompleted = resolvedOrganizationList.every((item) => {
      const orgId = item.organization?.id;
      return orgId ? orgSearchStatus[orgId]?.completed : false;
    });

    if (!allCompleted) {
      return;
    }

    const analysePayload = resolvedOrganizationList
      .map((item) => {
        const orgId = item.organization?.id;
        if (!orgId) return null;
        const status = orgSearchStatus[orgId];
        if (!status?.crmRecordId) {
          return null;
        }
        return {
          organization_id: orgId,
          crm_record_id: status.crmRecordId,
        };
      })
      .filter(Boolean);

    if (analysePayload.length === 0) {
      analyseTriggeredRef.current = true;
      return;
    }

    setCRMBulkAnalysePayload(analysePayload);
    setCRMBulkAnalyseData([]);
    runCRMAnalyse(
      {
        data: analysePayload,
        configId: activeCRMIntegration.id,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      },
      {
        onSettled: () => {},
      }
    );
    analyseTriggeredRef.current = true;
  }, [
    activeCRMIntegration?.id,
    orgSearchStatus,
    resolvedOrganizationList,
    runCRMAnalyse,
    setCRMBulkAnalyseData,
    setCRMBulkAnalysePayload,
    workspaceId,
  ]);

  console.log("companyConfigs", companyConfigs);

  return (
    <div className="relative">
      <Header homeLink="/contact-viewer/bulk-accounts" />
      <div className="space-y-3 p-3 pb-[85px]">
        {accounts.length > 0 && (
          <div className="">
             <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button  className={`inline-flex items-center text-xs h-8 ${selectedCount === 0 && "opacity-70"} mb-4`}>
                  <div className="flex w-full items-center py-1">
                    <span>{exportLabel}</span>
                    {isCSVUploadLoading && <div className="loader_circle inline-block ml-2" />}
                    <ChevronDownIcon className="ml-2 h-4 w-4" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent className="w-64" align="start">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      disabled={isExportDisabled}
                      onSelect={(event) => {
                        event.preventDefault();
                        if (isExportDisabled) return;
                        handleExportCompanies();
                      }}
                      className="py-2"
                    >
                      <div className="flex items-center w-full">
                        <Image
                          src="/images/icons/untitledui/csv-icon.svg"
                          width={18}
                          className="no-invert mr-2.5"
                        />
                        <span>Enrich & export as CSV</span>
                        {isCSVUploadLoading && <div className="loader_circle inline-block ml-2" />}
                      </div>
                    </DropdownMenuItem>
                    {activeCRMIntegration?.id &&
                      companyConfigs.length > 0 &&
                      companyConfigs.map((config) => {
                        const hasPermission =
                          companyConfigsWithPermissions.length === 0 ||
                          companyConfigsWithPermissions.find(
                            (item) => item.crm_object_api_name === config.crm_object_api_name
                          );
                        const disabled = !hasPermission || selectedCount === 0;

                        return (
                          <DropdownMenuItem
                            key={config.crm_object_api_name}
                            disabled={disabled}
                            onSelect={(event) => {
                              event.preventDefault();
                              if (disabled) {
                                if (!hasPermission) {
                                  toast.error("You do not have permission to create this object");
                                }
                                return;
                              }
                              handleOpenCompanyPush(config);
                              captureEvent("EXC_BulkAccounts_AddCRMObjectClick", {
                                crm_object_label: config?.crm_object_label,
                                crm_object_api_name: config?.crm_object_api_name,
                                selected_count: selectedCount,
                              });
                            }}
                            className="py-2"
                          >
                            <div className="flex items-center w-full">
                              <Image
                                src={`/images/logos/integrations/${activeCRMIntegration?.integration?.key}.svg`}
                                width={18}
                                className="no-invert mr-2.5"
                              />
                              <span className="mr-2">Add to CRM</span>
                              <Badge variant="outline" className="text-[10px]">
                                {config?.crm_object_label}
                              </Badge>
                            </div>
                          </DropdownMenuItem>
                        );
                      })}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={allSelectableSelected}
                indeterminate={isIndeterminate}
                onCheckedChange={(checked) => handleSelectAllChange(checked === true)}
                disabled={selectableAccountKeys.length === 0}
              />
              <span className="text-sm text-slate-600">
                {selectedCount} selected
                {selectableAccountKeys.length > 0 && ` / ${selectableAccountKeys.length}`}
              </span>
            </div>
           
          </div>
        )}

        {showSkeletons &&
          [1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={`skeleton-${i}`}
              className="py-4 pt-3.5 px-5 items-center border border-input bg-background rounded-[14px] relative overflow-hidden"
            >
              <div className="flex items-center space-x-3">
                <span className="h-10 w-10 rounded-full bg-slate-300" />
                <div>
                  <Skeleton className="h-[20px] w-[200px]" />
                  <span className="text-[12px] text-gray-900 flex items-center space-x-2 mt-0.5">
                    <Skeleton className="h-[15px] w-[70px] inline-flex" />
                    <Skeleton className="h-[15px] w-[70px] inline-flex" />
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <Skeleton className="h-[40px] w-[150px]" />
              </div>
            </div>
          ))}

        {accounts.length > 0 &&
          accounts.map((account, index) => {
            const accountKey = resolveAccountKey(account, index);
            return (
              <SingleAccount
                key={accountKey}
                account={account}
                workspaceId={workspaceId}
                activeWorkspace={activeWorkspace}
                accountKey={accountKey}
                orgSearchStatus={orgSearchStatus}
                isSelected={selectedAccountKeys.includes(accountKey)}
                onSelectChange={(checked) => handleAccountSelectionChange(accountKey, checked)}
                onOrganizationResolved={(payload) => handleOrgResolved(payload, accountKey)}
              />
            );
          })}
        {/* 
        {activeCRMIntegration?.id && resolvedOrganizationList.length > 0 && (
          <div className="border p-3 rounded-xl bg-background text-xs text-slate-600">
            <div className="text-sm font-semibold text-slate-900">CRM Sync</div>
            <div className="mt-1">
              {searchTriggeredRef.current
                ? allSearchCompleted
                  ? "CRM search completed"
                  : "Running CRM search..."
                : "Waiting for organization enrichment to finish"}
            </div>
            {searchTriggeredRef.current && (
              <div className="mt-1">
                {!hasCRMMatches
                  ? "No CRM matches found"
                  : analyseTriggeredRef.current
                    ? isCRMBulkAnalyseFetched
                      ? "CRM analyse completed"
                      : "Analysing CRM differences..."
                    : "Waiting to start CRM analyse..."}
              </div>
            )}
          </div>
        )} */}
      </div>
    </div>
  );
}
